"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.shouldCountryUseAppleMaps = shouldCountryUseAppleMaps;
exports.computeRoutes = computeRoutes;
exports.geoCodeAddress = geoCodeAddress;
exports.computeMultipleRoutes = computeMultipleRoutes;
exports.computeDistanceMatrix = computeDistanceMatrix;
exports.getUsState = getUsState;
const constants_1 = require("@daytrip/constants");
const legacy_config_1 = require("@daytrip/legacy-config");
const utils_1 = require("@daytrip/utils");
const node_fetch_1 = __importDefault(require("node-fetch"));
const maps_client_error_1 = require("../maps-client/maps-client-error");
const APPLE_TOKEN_URL = "https://maps-api.apple.com/v1/token";
let accessToken;
const stateIsoCodes = constants_1.US_STATES.map((state) => state.abbreviation.toLowerCase());
const COUNTRY_IDS_USING_APPLE_MAPS = ["2c92d86c-cf58-4c1e-a892-d468e7252edd"];
function shouldCountryUseAppleMaps(countryId) {
    return COUNTRY_IDS_USING_APPLE_MAPS.includes(countryId);
}
async function computeRoutes(origin, destination, intermediates) {
    if (!accessToken) {
        accessToken = await getAccessToken(legacy_config_1.APPLE_MAPS_TOKEN);
    }
    const allPoints = [origin, ...intermediates, destination];
    const legs = [];
    for (let i = 0; i < allPoints.length - 1; i++) {
        legs.push({ origin: allPoints[i], destination: allPoints[i + 1] });
    }
    const results = await Promise.all(legs.map((leg) => computeMultipleRoutes(leg.origin, [leg.destination])));
    return mergeEtaResults(results.map((result) => result[0]));
}
async function geoCodeAddress(address) {
    const url = `https://maps-api.apple.com/v1/geocode?q=${address}`;
    try {
        if (!accessToken) {
            accessToken = await getAccessToken(legacy_config_1.APPLE_MAPS_TOKEN);
        }
        let appleMapsResponse = await (0, node_fetch_1.default)(url, {
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
        });
        // refresh token and repeat the request
        if (appleMapsResponse.status === 401) {
            accessToken = await getAccessToken(legacy_config_1.APPLE_MAPS_TOKEN);
            appleMapsResponse = await (0, node_fetch_1.default)(url, {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            });
        }
        if (!appleMapsResponse.ok) {
            const message = "Apple Maps API returned non-OK response";
            throw new maps_client_error_1.MapsClientError(message, {
                url,
                status: appleMapsResponse.status,
                responseBody: await appleMapsResponse.json(),
            });
        }
        const responseBody = await appleMapsResponse.json();
        if (!responseBody?.results?.length) {
            const message = "Failed to geocode address using Apple Maps API";
            throw new maps_client_error_1.MapsClientError(message, {
                url,
                status: 200,
                responseBody,
            });
        }
        return responseBody.results[0];
    }
    catch (err) {
        if (err instanceof maps_client_error_1.MapsClientError) {
            throw err;
        }
        throw new maps_client_error_1.MapsClientError("Error when calling Apple Maps API", {
            url,
            err,
        });
    }
}
async function computeMultipleRoutes(origin, destinations) {
    if (destinations.length === 1 && origin.latitude === destinations[0].latitude && origin.longitude === destinations[0].longitude) {
        return [
            {
                distanceMeters: 0,
                durationSeconds: 0,
            },
        ];
    }
    const destinationStrings = destinations.map((destination) => `${destination.latitude},${destination.longitude}`);
    const url = `https://maps-api.apple.com/v1/etas?origin=${origin.latitude},${origin.longitude}&destinations=${destinationStrings.join("|")}`;
    try {
        let appleMapsResponse = await (0, node_fetch_1.default)(url, {
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
        });
        // refresh token and repeat the request
        if (appleMapsResponse.status === 401) {
            accessToken = await getAccessToken(legacy_config_1.APPLE_MAPS_TOKEN);
            appleMapsResponse = await (0, node_fetch_1.default)(url, {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            });
        }
        if (!appleMapsResponse.ok) {
            const message = "Apple Maps API returned non-OK response";
            throw new maps_client_error_1.MapsClientError(message, {
                url,
                status: appleMapsResponse.status,
                responseBody: await appleMapsResponse.json(),
            });
        }
        const etasResult = await appleMapsResponse.json();
        if (!etasResult.etas?.length ||
            etasResult.etas.length !== destinations.length ||
            etasResult.etas.some((eta) => (0, utils_1.isUndefinedOrNull)(eta.distanceMeters) || (0, utils_1.isUndefinedOrNull)(eta.staticTravelTimeSeconds))) {
            const message = "Failed to calculate route using Apple Maps API - no route found";
            throw new maps_client_error_1.MapsClientError(message, {
                url,
                status: 200,
                responseBody: etasResult,
            });
        }
        return etasResult.etas.map((eta) => ({
            distanceMeters: eta.distanceMeters,
            durationSeconds: eta.staticTravelTimeSeconds,
        }));
    }
    catch (err) {
        if (err instanceof maps_client_error_1.MapsClientError) {
            throw err;
        }
        throw new maps_client_error_1.MapsClientError("Error when calling Apple Maps API", {
            url,
            err,
        });
    }
}
async function computeDistanceMatrix(locations) {
    try {
        const routeMetadata = await Promise.all(locations.map((location) => computeMultipleRoutes(location, locations)));
        if (routeMetadata.length !== locations.length || routeMetadata.some((result) => result.length !== locations.length)) {
            throw new maps_client_error_1.MapsClientError("Failed to compute distance matrix - an ETA is missing", {
                locations,
                etaResults: routeMetadata,
            });
        }
        return routeMetadata;
    }
    catch (err) {
        if (err instanceof maps_client_error_1.MapsClientError) {
            throw err;
        }
        throw new maps_client_error_1.MapsClientError("Failed to compute distance matrix", {
            locations,
            err,
        });
    }
}
async function getAccessToken(appleMapsToken) {
    const accessTokenResponse = await (0, node_fetch_1.default)(APPLE_TOKEN_URL, {
        headers: {
            Authorization: `Bearer ${appleMapsToken}`,
        },
    });
    if (!accessTokenResponse.ok) {
        const message = "Failed to get Apple Maps access token";
        throw new maps_client_error_1.MapsClientError(message, {
            url: APPLE_TOKEN_URL,
            status: accessTokenResponse.status,
            responseBody: await accessTokenResponse.json(),
        });
    }
    const json = await accessTokenResponse.json();
    return json.accessToken;
}
async function fetchWithRetry(url, token) {
    try {
        // eslint-disable-next-line @typescript-eslint/return-await
        return await (0, node_fetch_1.default)(url, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
    }
    catch (error) {
        console.error("Error occurred while fetching:", error);
        return (0, node_fetch_1.default)(url, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
    }
}
async function getUsState(position) {
    // eslint-disable-next-line @typescript-eslint/no-shadow
    let accessToken = await getAccessToken(legacy_config_1.APPLE_MAPS_TOKEN);
    const url = `https://maps-api.apple.com/v1/reverseGeocode?loc=${position.latitude},${position.longitude}`;
    let appleMapsResponse = await fetchWithRetry(url, accessToken);
    // refresh token
    if (appleMapsResponse.status === 401) {
        accessToken = await getAccessToken(legacy_config_1.APPLE_MAPS_TOKEN);
        appleMapsResponse = await fetchWithRetry(url, accessToken);
    }
    if (!appleMapsResponse.ok) {
        throw new maps_client_error_1.MapsClientError("Failed to get state for location", { responseStatus: appleMapsResponse.status });
    }
    const appleMapsJson = await appleMapsResponse.json();
    if (appleMapsJson.results.length === 0) {
        throw new maps_client_error_1.MapsClientError("No results found for location", { response: appleMapsJson });
    }
    const { administrativeAreaCode, administrativeArea } = appleMapsJson.results[0].structuredAddress;
    if (!administrativeAreaCode || !administrativeArea) {
        throw new maps_client_error_1.MapsClientError("No state found for location", { response: appleMapsJson });
    }
    if (!stateIsoCodes.includes(administrativeAreaCode?.toLowerCase())) {
        throw new maps_client_error_1.MapsClientError("No valid state found for location", { administrativeAreaCode });
    }
    const stateName = constants_1.US_STATES.find((state) => state.abbreviation.toLowerCase() === administrativeAreaCode.toLowerCase())?.name ?? "";
    return { stateName, stateCode: administrativeAreaCode };
}
function mergeEtaResults(routeMetadata) {
    const distanceMeters = routeMetadata.reduce((sum, metadata) => sum + metadata.distanceMeters, 0);
    const durationSeconds = routeMetadata.reduce((sum, metadata) => sum + metadata.durationSeconds, 0);
    const legs = routeMetadata.map((metadata) => ({
        distanceMeters: metadata.distanceMeters,
        durationSeconds: metadata.durationSeconds,
    }));
    return {
        distanceMeters,
        durationSeconds,
        legs,
    };
}
//# sourceMappingURL=apple-maps-client.js.map